import { Edge, Node, XYPosition } from "@xyflow/react";
import { SequenceNodeData } from "./SequenceNode";
import { createSequenceEdgeId } from "./SequenceEdgeIds";

export const EDGE_INSERTION_DROP_THRESHOLD = 48;
export const SEQUENCE_NODE_DEFAULT_WIDTH = 256;
export const SEQUENCE_NODE_DEFAULT_HEIGHT = 96;

export type AltDragPhase = "normal" | "detached" | "connected";

export type AltDragState = {
  nodeId: string;
  phase: AltDragPhase;
};

export type EdgePathGeometry = {
  points: XYPosition[];
};

export function isTemporaryEdge(edge: Edge): boolean {
  return edge.id?.startsWith("temp-connection-") ?? false;
}

export function isSpliceableEdge(edge: Edge): boolean {
  return !isTemporaryEdge(edge);
}

export function createCustomEdge(
  source: string,
  target: string,
  sourceHandle: string | null = null,
  targetHandle: string | null = null,
): Edge {
  return {
    id: createSequenceEdgeId(source, target),
    source,
    target,
    sourceHandle,
    targetHandle,
    type: "custom",
  };
}

export function getSpliceableEdgesForNode(edges: Edge[], nodeId: string) {
  const eligibleEdges = edges.filter(isSpliceableEdge);
  return {
    incoming: eligibleEdges.filter((edge) => edge.target === nodeId),
    outgoing: eligibleEdges.filter((edge) => edge.source === nodeId),
  };
}

export function extractNodeFromSpliceableChain(
  edges: Edge[],
  nodeId: string,
): Edge[] | null {
  const { incoming, outgoing } = getSpliceableEdgesForNode(edges, nodeId);
  if (incoming.length !== 1 || outgoing.length !== 1) {
    return null;
  }

  const [incomingEdge] = incoming;
  const [outgoingEdge] = outgoing;
  const remainingEdges = edges.filter(
    (edge) => edge.id !== incomingEdge.id && edge.id !== outgoingEdge.id,
  );

  return [
    ...remainingEdges,
    createCustomEdge(
      incomingEdge.source,
      outgoingEdge.target,
      incomingEdge.sourceHandle ?? null,
      outgoingEdge.targetHandle ?? null,
    ),
  ];
}

export function insertNodeIntoEdge(
  edges: Edge[],
  edgeToSplit: Edge,
  nodeId: string,
): Edge[] {
  const remainingEdges = edges.filter((edge) => edge.id !== edgeToSplit.id);

  return [
    ...remainingEdges,
    createCustomEdge(
      edgeToSplit.source,
      nodeId,
      edgeToSplit.sourceHandle ?? null,
      null,
    ),
    createCustomEdge(
      nodeId,
      edgeToSplit.target,
      null,
      edgeToSplit.targetHandle ?? null,
    ),
  ];
}

export function getNodeDimension(
  node: Node<SequenceNodeData>,
  axis: "width" | "height",
): number | null {
  const measuredValue = node.measured?.[axis];
  if (typeof measuredValue === "number" && measuredValue > 0) {
    return measuredValue;
  }

  const directValue = node[axis];
  if (typeof directValue === "number" && directValue > 0) {
    return directValue;
  }

  return axis === "width"
    ? SEQUENCE_NODE_DEFAULT_WIDTH
    : SEQUENCE_NODE_DEFAULT_HEIGHT;
}

export function getNodeCenterPoint(
  node: Node<SequenceNodeData>,
): XYPosition | null {
  const width = getNodeDimension(node, "width");
  const height = getNodeDimension(node, "height");
  if (width === null || height === null) {
    return null;
  }

  return {
    x: node.position.x + width / 2,
    y: node.position.y + height / 2,
  };
}

export function updateNodePositionInList(
  nodes: Node<SequenceNodeData>[],
  updatedNode: Node<SequenceNodeData>,
): Node<SequenceNodeData>[] {
  return nodes.map((currentNode) =>
    currentNode.id === updatedNode.id
      ? {
          ...currentNode,
          position: { ...updatedNode.position },
        }
      : currentNode,
  );
}

function getDistanceToSegment(
  point: XYPosition,
  start: XYPosition,
  end: XYPosition,
): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  if (dx === 0 && dy === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }

  const projection =
    ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy);
  const t = Math.max(0, Math.min(1, projection));
  const projectedPoint = {
    x: start.x + t * dx,
    y: start.y + t * dy,
  };

  return Math.hypot(point.x - projectedPoint.x, point.y - projectedPoint.y);
}

export function findSpliceableEdgeNearPoint(
  edges: Edge[],
  edgePathGeometryById: Map<string, EdgePathGeometry>,
  point: XYPosition,
): Edge | null {
  let closestEdge: Edge | null = null;
  let closestDistance = EDGE_INSERTION_DROP_THRESHOLD;

  edges.forEach((edge) => {
    if (!isSpliceableEdge(edge)) {
      return;
    }

    const geometry = edgePathGeometryById.get(edge.id);
    if (!geometry || geometry.points.length < 2) {
      return;
    }

    let distance = Number.POSITIVE_INFINITY;
    for (let index = 0; index < geometry.points.length - 1; index += 1) {
      const segmentDistance = getDistanceToSegment(
        point,
        geometry.points[index],
        geometry.points[index + 1],
      );
      distance = Math.min(distance, segmentDistance);
    }

    if (distance <= closestDistance) {
      closestDistance = distance;
      closestEdge = edge;
    }
  });

  return closestEdge;
}
